import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { FieldText } from 'components/Fields/Text';
import { Icon, Input, UserPreview } from 'components/UI';
import { CountdownTimer } from 'components/UI/CountdownTimer';
import { Switch } from '@repo/ui';
import { Search, Gift } from 'lucide-react';

import { confirmTransferCertificate } from 'store/certificates/actions';
import { useDispatch } from 'store/hooks';
import { getName } from 'utils/parsers';

import type { CertificateCustomer, Customer } from 'types';
import { RecipientSearchPicker } from './RecipientSearchPicker';
import { fieldSurfaceSx, FIELD_HEIGHT } from 'components/UI/fieldMetrics';
import { FieldLabel } from 'components/UI/FieldLabel';
import { font } from 'settings/tokens';
import { ScanTag } from './ScanTag';
import { SuccessOwnership } from './SuccessOwnership';

import { Box, Stack, Typography, Button } from '@mui/material';
import { ProductCard } from 'components/productCard';
import { useTransferCustomerInit } from 'hooks/api/transfers/useInitTransfer';
import { useGetCertificatePublic } from 'hooks/api/useGetCertificatePublic';
import {
  useProvenanceConsent,
  useUpdateProvenanceConsent,
} from 'hooks/api/useProvenanceConsent';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from 'routes/routes';
import { glassSx } from 'utils/glass'

interface GiftCertificateProps {
  certificate: CertificateCustomer;
  closeGift: () => void;
  onExpired?: () => void;
}

export const GiftCertificate = ({ certificate, closeGift, onExpired }: GiftCertificateProps) => {
  const methods = useForm();
  const dispatch = useDispatch();
  const { resetField } = methods;
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data: certificateData} = useGetCertificatePublic(certificate.id);

  const {
    mutate: transferCustomerInit,
    isPending: isTransferCustomerInitPending,
    isSuccess: isTransferCustomerInitSuccess,
  } = useTransferCustomerInit();

  const [selectedUser, setSelectedUser] = useState<Customer | null>(null);
  const [isExpired, setIsExpired] = useState<boolean>(false);
  const [isAnonymous, setIsAnonymous] = useState<boolean>(false);
  const [giftMessage, setGiftMessage] = useState<string>('');
  const [showNotice, setShowNotice] = useState<boolean>(true);
  const [openRecipientSearch, setOpenRecipientSearch] = useState<boolean>(false);
  const [openScanTag, setOpenScanTag] = useState<boolean>(false);
  const [isTagScanned, setIsTagScanned] = useState<boolean>(false);
  const [openTransferOwnership, setOpenTransferOwnership] = useState<boolean>(false);

  /*
   * VRG-511 — the sender's consent to being named on THIS item's public chain.
   *
   * ⚠️ IT GOVERNS THE SENDER'S OWN HOP, NOT THE ONE BEING CREATED. That is the
   * non-obvious part of the model and the reason there is no field for it on the
   * transfer payload below. The transfer about to be created names the RECIPIENT,
   * so a flag on it would be the sender deciding whether the RECIPIENT is named --
   * which is precisely the "whose consent is being spent" defect this whole ticket
   * exists to remove. What the sender is deciding is whether THEIR name stays on
   * the chain once the item has moved on, and that lives on the hop where they
   * acquired it. Every sender has one, including the first owner out of the brand:
   * the `primary` transfer names them as its `to_owner_id`.
   *
   * Seeded from the stored value rather than hardcoded `true`, so someone who
   * already opted out on this item does not silently opt back IN by gifting it.
   * `?? true` covers the window before the query settles and matches the column
   * default.
   */
  const { data: provenanceConsent } = useProvenanceConsent(certificate.id);
  const updateProvenanceConsent = useUpdateProvenanceConsent(certificate.id);
  const [sharesProvenanceIdentity, setSharesProvenanceIdentity] =
    useState<boolean>(true);
  const storedSharesIdentity = provenanceConsent?.shares_identity;

  useEffect(() => {
    if (typeof storedSharesIdentity === 'boolean') {
      setSharesProvenanceIdentity(storedSharesIdentity);
    }
  }, [storedSharesIdentity]);

  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: ['certificate-public', certificate.id] });
  }, [isExpired]);

  const clearSearch = () => {
    setSelectedUser(null);
    resetField('search');
  };

  const expireHandler = () => {
    setIsExpired(true);
    onExpired && onExpired();
  };

  const certificateStatus = certificateData?.status || certificate?.status;
  const transferInitiatedStatus = certificateStatus === 'transfer_initiated';

  /**
   * VERA-304: refresh the certificate list + this certificate's public record.
   * Called when the user DISMISSES the gift confirmation, never while it is on
   * screen — the mutation used to do this itself and destroyed the very drawer
   * showing the confirmation (see useInitTransfer for the measured evidence).
   */
  const flushCertificateQueries = () => {
    queryClient.invalidateQueries({ queryKey: ['certificate-public', certificate.id] });
    queryClient.invalidateQueries({ queryKey: ['certificates'] });
  };

  const onSubmit = () => {
    // VRG-358 — was `if (!selectedUser?.email) return`, which silently did
    // NOTHING for any recipient picked by @handle, QR scan, or recent
    // recipients. VRG-357 removed `email` from the lookup projection (returning
    // it made the lookup an enumeration oracle), so those recipients arrive
    // with no address by design — and this guard rejected every one of them
    // without a message, a toast, or a console line. Gate on the recipient
    // existing, then address by whichever identifier we actually hold.
    if (!selectedUser) return;

    try {
      transferCustomerInit({
        certificateId: certificate.id,
        data: {
          // The invite-by-email card is the ONLY path that carries an email
          // (it fabricates one from what the sender typed). Everything else
          // addresses by the owner id the lookup returned for exactly this
          // purpose.
          ...(selectedUser.email
            ? { toCustomerEmail: selectedUser.email }
            : { toCustomerId: selectedUser.id }),
          type: 'gift',
          isAnonymous,
          giftMessage: giftMessage.trim() || undefined,
        },
      });

      /*
       * VRG-511 — write the consent only when it CHANGED, and only here.
       *
       * Here, rather than on toggle, so the preference commits with the gift: this
       * sheet reads as one decision, and a control that persisted the instant it
       * moved would leave a changed setting behind after a cancelled gift.
       *
       * Only when changed, because an unconditional PATCH on every gift would
       * rewrite `updated_at` on the sender's hop for no reason -- and, for a sender
       * whose query has not settled, would write the `true` default over a `false`
       * they had already chosen. `storedSharesIdentity` is undefined until it
       * settles, so this comparison is false then and nothing is written.
       *
       * Fire-and-forget alongside the transfer rather than awaited: the two are
       * independent rows, and a consent write that failed must not block or roll
       * back a gift the user actually made. It stays correctable from the
       * previously-owned card afterwards.
       */
      if (
        typeof storedSharesIdentity === 'boolean' &&
        sharesProvenanceIdentity !== storedSharesIdentity
      ) {
        updateProvenanceConsent.mutate(sharesProvenanceIdentity);
      }

      setOpenTransferOwnership(true);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: '500px',
      }}
    >
      {/* VERA-304 round 4: ALL section-to-section gaps now come from ONE place
          -- this Stack's own spacing={3} (24px) -- instead of each section
          fighting it with a local `marginTop: '16px !important'` override.
          Every section below (Item / Recipient / Send anonymously / Gift
          message) is a bare, unstyled Box that's a DIRECT Stack child, so the
          Stack's spacing governs uniformly. The one exception is the Gift
          button below, which keeps its own larger 32px gap -- that's a
          deliberate call-to-action separation, not a section boundary. */}
      <Stack direction='column' spacing={3} sx={{ flex: 1, pb: 2 }}>
        {/* VERA-304 round 3: this section's own mb={2} on the label was DEAD —
            as a direct Stack child it was overridden by MUI Stack's own
            spacing rule (`.MuiStack-root > :not(style) ~ :not(style) { margin:
            0; margin-top: 24px }`, higher specificity than the label's own sx
            class), so the visible label-to-card gap was actually 24px from the
            Stack, not the 16px the mb prop declared. Wrapping the label +
            content in their own Box (as every section here now does) lets the
            label's own mb apply for the internal label-to-content gap, while
            the Stack's spacing governs the gap BETWEEN sections. */}
        <Box>
          {/*
            FieldLabel, like every other label in the app. These were `variant='h6'`
            overridden back down to 16/500 — a sixth hand-rolled label treatment,
            and one the filter-sheet sweep did not reach.
          */}
          <FieldLabel label='Item' />

          {/*
            NO card around this one.

            Item and Recipient shipped in the SAME PreviewCard, which is what made
            the pair read as redundant boxes — but they are not the same kind of
            thing. Recipient is a field: you tap it, it opens a picker, it holds a
            value you can clear. Item is not. It is read-only context naming which
            certificate you are gifting, and there is exactly one of it, already
            named by the label above. A border around a single unchangeable row
            says "this is a control" about something you cannot operate.

            So the box comes off here and stays on Recipient, which is the opposite
            of removing both: the remaining border now MEANS something (this is the
            field) instead of being applied to everything uniformly and therefore
            saying nothing.
          */}
          <ProductCard product={certificate.product} bare />
        </Box>

        <Box>
          {/* VERA-304 round 4: "To" -> "Recipient" -- parallel with "Item"
              (both nouns naming what the field holds), where "To" read as a
              preposition/label mismatch. */}
          <FieldLabel label='Recipient' />

          {!selectedUser ? (
            // VERA-304 verify-walkthrough feedback (B5): the sheet's
            // "Recipient" section is just a tap-target that opens
            // RecipientSearchPicker — the search input AND its empty-state
            // copy live only in that drawer, so they aren't duplicated here.
            //
            // A field-shaped TRIGGER, not a Button. It was a `<Button variant='outlined'>`
            // dressed to look like a field, and measured 14px against every real field's
            // 16px — because button typography, not field typography, was deciding its
            // size. Sitting directly above the 16px gift-message textarea, that 2px
            // mismatch is exactly what read as "this doesn't match the other fields".
            //
            // `fieldSurfaceSx` + `FIELD_HEIGHT` is the shared answer for precisely this
            // case — see its doc: "every field-shaped control that is NOT the shared
            // Input — the search trigger, the date trigger". Same pattern as
            // CertificatesSearchPicker's trigger. It also fixes the radius for free: the
            // hardcoded 8px bypassed the clamp, which resolves to 6.
            <Box
              onClick={() => setOpenRecipientSearch(true)}
              sx={theme => ({
                ...fieldSurfaceSx(theme),
                height: FIELD_HEIGHT,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                cursor: 'pointer',
                color: 'text.secondary',
                // The canonical field size (font.body). Also the iOS auto-zoom floor —
                // this one is not focusable, but it must LOOK like the fields it sits with.
                fontSize: `${font.body}px`,
                '&:hover': { borderColor: theme.palette.grey[400] },
              })}
            >
              {/* VERA-304: lucide `Search` at 16, NOT MUI's `SearchIcon fontSize='small'`
                  (20px, a heavier filled glyph). The surface already matched via
                  `fieldSurfaceSx` + FIELD_HEIGHT and the label already used font.body,
                  so the ONLY thing still differing between this trigger and the search
                  box inside RecipientSearchPicker — the field it opens — was the icon:
                  two different glyph families at two different sizes. */}
              <Search size={16} />
              Search
            </Box>
          ) : (
            // A FIELD SURFACE, not a PreviewCard — matching the empty state directly
            // above it, which is already `fieldSurfaceSx`.
            //
            // Filling the recipient used to swap a filled field for a white bordered
            // card, so the same control changed material the moment it held a value,
            // and then sat in a form where Search and the gift-message textarea are
            // both filled. `fieldSurfaceSx` is the shared answer for exactly this
            // ("every field-shaped control that is NOT the shared Input") and keeps
            // the empty and filled states one control.
            //
            // Height is NOT FIELD_HEIGHT here: UserPreview is a two-line avatar row
            // and taller than a one-line field by nature. The surface is what has to
            // match, not the height.
            <Box
              sx={theme => ({
                ...fieldSurfaceSx(theme),
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                p: 2,
              })}
            >
              {/* VERA-304 verify-walkthrough feedback (B3): the selected recipient is
                  the same avatar+name+subtext unit as the search results, so it renders
                  through the shared UserPreview rather than hand-rolling a third copy
                  (§36). Email/phone are deliberately NOT shown — only name + @username.
                  A recipient added by email (not yet a member) has no name or username,
                  so the email is the only identifier we can show; that is the one case
                  where it appears, and it is the address the user just typed themselves. */}
              {/* VERA-304: identity comes from the shared resolver, not from a
                  chain re-written per call site. This one used to render the
                  username as BOTH lines ("spencersedb" / "@spencersedb") for a
                  person with no first/last name. */}
              <UserPreview user={selectedUser} avatarUrl={selectedUser.avatar} />

              <Icon
                name='close'
                sx={theme => ({
                  color: theme.palette.grey[600],
                  cursor: 'pointer',
                  '&:hover': { color: theme.palette.grey[800] },
                })}
                onClick={clearSearch}
              />
            </Box>
          )}
        </Box>

        <Box>
          {/* VERA-304 round 4: back to alignItems='center' -- centers the
              Switch against the full title+caption block. The prior
              flex-start change (aligning it to just the title line) is
              reverted per explicit feedback. */}
          <Stack direction='row' alignItems='center' justifyContent='space-between'>
            <Box>
              {/* VERA-315: the same `FieldLabel` as Item / Recipient / Gift message.
                  This was `variant='h6'` pulled back down to 16/500 by hand — a
                  label treatment that only LOOKED like its neighbours because two
                  separate places happened to agree on the same two numbers. */}
              <FieldLabel label='Send anonymously' />
              {/* VERA-304 round 2: helper/secondary copy floor is 14px.
                  VERA-315: that floor is what `body2` IS (14/1.45), so the variant
                  states it instead of a `caption` (12px, undefined in this theme so
                  it falls through to MUI's default) overridden back up to 14. */}
              <Typography variant='body2' color='text.secondary'>
                Hide your name from the recipient.
              </Typography>
            </Box>
            <Switch checked={isAnonymous} onChange={(_, checked) => setIsAnonymous(checked)} />
          </Stack>
        </Box>

        {/* VRG-511 — the sender's name on the PUBLIC chain.

            Adjacent to "Send anonymously" and easily confused with it, so the copy
            has to do the separating: that one hides the sender from the RECIPIENT
            (one person, this handover), this one governs whether the sender is named
            on the item's public history (everyone, forever). Both are about the
            sender's name, which is exactly why each says WHO sees it.

            No `Box` wrapper of its own and no local margins: the parent Stack's
            `spacing={3}` owns the gap between fields here, and adding one would
            double-count it (see the note at the top of this component's Stack).

            Markup matches the "Send anonymously" row above verbatim -- FieldLabel +
            body2 secondary + @repo/ui Switch. This surface has no Figma. */}
        <Box>
          <Stack direction='row' alignItems='center' justifyContent='space-between'>
            <Box>
              <FieldLabel label="Show my name in this item's history" />
              <Typography variant='body2' color='text.secondary'>
                {sharesProvenanceIdentity
                  ? 'Anyone who opens this item can see you owned it. You can change this later.'
                  : 'You will appear as "Previous owner" to anyone who opens this item.'}
              </Typography>
            </Box>
            <Switch
              checked={sharesProvenanceIdentity}
              onChange={(_, checked) => setSharesProvenanceIdentity(checked)}
            />
          </Stack>
        </Box>

        <Box>
          {/* VERA-304 verify-walkthrough feedback: the label needs the same
              treatment as "Recipient"/"Item"/"Send anonymously".
              VERA-315: it now gets it by BEING that component. The old note said
              the shared `Input`'s built-in `label` "renders a plain body2" so the
              label was hand-rolled out here and the Input left unlabeled — that
              was true and is now fixed at the source: `Input` delegates its label
              to `FieldLabel`. The label still lives here only because the
              "(optional)" qualifier reads better attached to the label than
              threaded through the Input's props.
              The qualifier's fainter treatment is `FieldLabel`'s `optional` prop
              (one source, §31), not a second hand-rolled span. */}
          <FieldLabel label='Gift message' optional />
          <Input
            placeholder='Add a note for the recipient'
            multiline
            minRows={2}
            maxRows={4}
            value={giftMessage}
            onChange={setGiftMessage}
            inputProps={{ maxLength: 500 }}
          />
        </Box>

        {/* VERA-304: the cancel notice moved from the TOP of the sheet to here, just
            above the Gift button. At the top it was the first thing read, so the sheet
            opened by explaining how to undo an action the user had not taken yet — and
            it pushed Item and Recipient, the two fields they came to fill, down the
            fold. It belongs next to the commit point, where "you can still undo this"
            is the reassurance that actually matters.
            @repo/ui Alert (severity="info"), matching the platform's tag-shop
            customization alerts rather than a hand-rolled box. */}
        {/*
          INSTRUCTIONS, not a dismissible alert.

          An `onClose` says "this is a notice that has served its purpose and can be
          put away". This sentence is neither: it is standing guidance about what the
          button beneath it does, it is true every time the form is opened, and it is
          most useful to the person who has never sent a gift — i.e. exactly the
          person who has not yet dismissed it. A close affordance on permanent help
          text only offers the reader a way to hide the thing they needed.

          Rendered as plain helper copy under the field it qualifies, which is what
          it always was.
        */}
        {/* VERA-315: the inline `lineHeight: 1.5` is gone. `body2` already declares
            1.45, and the two differ by 0.7px on a 14px line — the override bought
            nothing readable and cost the sheet a seventh line-height value. Helper
            copy on this sheet now leads uniformly at body2's own 1.45. */}
        <Typography variant='body2' color='text.secondary'>
          You can cancel this gift anytime before the recipient claims ownership.
        </Typography>
      </Stack>

      {/* VERA-304: the commit button is PINNED, not part of the scrolling column.
          Measured before this change: the button occupied 771–815px in an 812px
          viewport, i.e. its bottom 3px sat past the edge and it could not be
          tapped without first scrolling — on the one control the sheet exists to
          offer.

          `position: sticky; bottom: 0` is the exact mirror of the sticky HEADER
          that `DrawerPopup` already applies at `top: 0`, and it works for the
          same documented reason: on a non-fullScreen bottom sheet the Drawer
          Paper is the scroll owner (`maxHeight: 90vh` + MUI's default
          `overflowY: auto`), so a descendant can stick to its scrollport.
          The opaque background is required for the same reason it is on the
          header — scrolled content must pass BEHIND the bar, not show through it.

          It sits OUTSIDE the content `Stack` so the Stack's `spacing={3}` governs
          only the form sections; this bar owns its own padding. */}
      <Box
        sx={theme => ({
          position: 'sticky',
          bottom: 0,
          zIndex: 2,
          /* Glass, like every other pinned bar (utils/glass). A solid fill on a
             bar that content scrolls under reads as a separate panel bolted to the
             bottom; the blur is what says it is floating OVER the page. */
          ...glassSx(theme),
          pt: 2,
          pb: 1,
        })}
      >
        <Button
          fullWidth
          variant='contained'
          onClick={() => {
            if (isTagScanned) {
              dispatch(
                confirmTransferCertificate({ certificateId: certificate.id }, ({ success }) => {
                  if (success) {
                    queryClient.invalidateQueries({ queryKey: ['certificate-public', certificate.id] });
                    setOpenTransferOwnership(true);
                  }
                })
              );
            } else {
              setTimeout(() => setOpenScanTag(true), 50);
            }
          }}
          disabled={!selectedUser}
          sx={theme => ({
            backgroundColor: theme.palette.common.black,
            color: theme.palette.common.white,
            borderRadius: `${theme.shape.borderRadius}px`,
            py: 1.5,
            '&:hover': {
              backgroundColor: theme.palette.grey[800],
            },
            // VERA-304: NO per-call-site `&:disabled` here. The theme owns the
            // disabled primary (a slight tint of the button's own colour, not a
            // grey slab) and this override was silently beating it.
            //
            // Note it beat the theme even though the theme rule is a CLASS
            // selector (`.Mui-disabled`) and this was the PSEUDO-class
            // (`:disabled`): the two have identical specificity, so source order
            // decided, and the call-site sx is emitted last. That is why the
            // theme-level fix appeared to do nothing and the bug kept coming
            // back — it can only be fixed by deleting the local override.
            //
            // The old `marginTop: '32px !important'` is gone with the sticky bar:
            // the CTA separation it bought is now the bar's own `pt`, and a
            // 32px margin on a pinned element just shrinks the visible content.
          })}
        >
          Gift
        </Button>
      </Box>

      <RecipientSearchPicker
        open={openRecipientSearch}
        onClose={() => setOpenRecipientSearch(false)}
        onSelectRecipient={setSelectedUser}
      />

      {/* VERA-304: the gift TERMINATES in this drawer. It used to open a third sheet
          (GiftSendDrawer) on top of this one on top of the Gift form — and since both
          that sheet and the form were titled "Gift", the success screen appeared as a
          second identical header sliding over the first. Same task, one surface: the
          scan circle's glyph morphs into a gift, the copy becomes the confirmation, and
          "View certificate" replaces the scan affordances.

          `successTitle` also switches off ScanTag's 2s auto-close — this is now a
          destination the user dismisses, not a step passed through. */}
      {/* VRG-323: the brand's scan instructions + media are read off the
          `certificate` PROP, not the `certificateData` refetch above it. The prop
          is the typed `CertificateCustomer` this component is rendered with and is
          present on the first paint; the query's data is `any`-shaped and, on a
          cold arrival, undefined for as long as the request takes — which is
          exactly when the user is most likely to open "Where to scan?". Both carry
          the same already-resolved product fields, so the earlier one wins. */}
      {/* VERA-304: `successStagedReveal` below — the confirmation arrives as a SEQUENCE
          rather than in one frame: the circle checks off the SCAN, an arrow leads down,
          then the gift outcome lands. The gift flow is the case that ordering was built
          for. The user has just performed a physical action, and the screen should
          acknowledge THAT before it reports what the action caused; swapping the circle
          straight to a gift glyph never confirms the scan at all. */}
      <ScanTag
        title='Confirm gift'
        scanInstructions={certificate.product?.scan_instructions}
        scanMediaUrl={certificate.product?.scan_media_url}
        open={openScanTag}
        onClose={() => {
          setOpenScanTag(false);
          // A completed gift means the form behind this drawer is stale — close the
          // whole stack rather than returning the user to a sheet for a gift they
          // already sent.
          if (isTagScanned) {
            closeGift();
            // VERA-304: the list refresh happens HERE, on dismiss, not in the
            // mutation's onSuccess — see useInitTransfer for the measurement.
            // Invalidating while the confirmation was on screen refetched the
            // list, changed this card's data, and tore down the drawer stack
            // that was showing the confirmation.
            flushCertificateQueries();
          }
        }}
        onScanSuccess={() => {
          setIsTagScanned(true);
          onSubmit();
        }}
        successIcon={<Gift size={40} />}
        successStagedReveal
        successTitle='Gift sent'
        successDescription='A notification to confirm ownership is sent to the recipient. You can cancel this gift anytime before the recipient claims ownership.'
        successContent={<ProductCard product={certificate.product as any} />}
        successActionLabel='View certificate'
        successActionOnClick={() => {
          setOpenScanTag(false);
          closeGift();
          flushCertificateQueries();
          navigate(`/certificates/${certificate.id}`);
        }}
      />
    </Box>
  );
};
